import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { Button, Field, Input, Select, cx } from '../ui/WorkspaceUI';
import {
  ATTACHMENT_KINDS,
  EMPTY_FIELDS,
  SEARCH_CATEGORIES,
  buildQuery,
} from '../../utils/mailSearchQuery';
import {
  ACTION_TOGGLES,
  FILTER_CATEGORIES,
  suppressedActions,
  toPayload,
  validateDraft,
} from '../../utils/mailFilters';

/**
 * Create/edit form for one smart filter.
 *
 * Conditions are built with the same field set as advanced search — the filter's
 * conditions *are* a search query — so anything findable is also filterable, and
 * the generated query is shown so the user can see (and later hand-edit) it.
 *
 * The "Test" button previews which existing mail the conditions match before the
 * filter is saved, because a rule that deletes mail is a bad thing to get wrong.
 */
const FilterEditor = ({ draft, onChange, onSave, onCancel, saving, ownerEmail }) => {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [testing, setTesting] = useState(false);

  const query = useMemo(() => buildQuery(draft.fields), [draft.fields]);
  const errors = useMemo(() => validateDraft(draft, { ownerEmail }), [draft, ownerEmail]);
  const suppressed = useMemo(() => suppressedActions(draft.actions), [draft.actions]);

  // A stale preview beside changed conditions is worse than none.
  useEffect(() => {
    setPreview(null);
    setPreviewError('');
  }, [query]);

  const setField = (key) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    onChange({ ...draft, fields: { ...draft.fields, [key]: value } });
  };

  const setAction = (key) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    onChange({ ...draft, actions: { ...draft.actions, [key]: value } });
  };

  const runTest = async () => {
    setTesting(true);
    setPreviewError('');
    try {
      const { data } = await axiosInstance.post('/api/filters/preview', { query, limit: 10 });
      setPreview(data);
    } catch (error) {
      setPreviewError(error.response?.data?.message || 'Could not test these conditions');
    } finally {
      setTesting(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (errors.length) return;
    onSave(toPayload(draft));
  };

  return (
    <form onSubmit={submit} className="ws-glass rounded-xl p-4">
      <h3 className="text-sm font-semibold text-ink">
        {draft._id ? 'Edit filter' : 'New filter'}
      </h3>

      <Field label="Name" hint="Optional — shown in the filter list" className="mt-3">
        <Input
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
          placeholder="Newsletters"
        />
      </Field>

      <p className="mt-4 text-xs font-medium uppercase tracking-wide text-ink-soft">
        When a message arrives that matches
      </p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="From">
          <Input value={draft.fields.from} onChange={setField('from')} placeholder="news@shop.com" />
        </Field>
        <Field label="To">
          <Input value={draft.fields.to} onChange={setField('to')} placeholder="me@atozasmail.in" />
        </Field>
        <Field label="Subject" className="sm:col-span-2">
          <Input value={draft.fields.subject} onChange={setField('subject')} placeholder="Contains these words" />
        </Field>
        <Field label="Has the words">
          <Input value={draft.fields.hasWords} onChange={setField('hasWords')} placeholder="invoice receipt" />
        </Field>
        <Field label="Doesn't have">
          <Input value={draft.fields.notWords} onChange={setField('notWords')} placeholder="unsubscribe" />
        </Field>
        <Field label="Attachment type">
          <Select value={draft.fields.attachmentKind} onChange={setField('attachmentKind')} className="w-full">
            {ATTACHMENT_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={draft.fields.category} onChange={setField('category')} className="w-full">
            {SEARCH_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Larger than" hint="e.g. 5M">
          <Input value={draft.fields.largerThan} onChange={setField('largerThan')} placeholder="5M" />
        </Field>
        <Field label="Smaller than" hint="e.g. 1M">
          <Input value={draft.fields.smallerThan} onChange={setField('smallerThan')} placeholder="1M" />
        </Field>
      </div>

      <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={!!draft.fields.attachment}
          onChange={setField('attachment')}
          disabled={!!draft.fields.attachmentKind}
          className="ws-focus h-4 w-4 accent-[var(--ws-accent)]"
        />
        Has an attachment
      </label>

      <div className="mt-3 rounded-lg bg-muted px-3 py-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-soft">Conditions</span>
        <code className="mt-1 block break-words font-mono text-xs text-ink">
          {query || 'Fill in a field above'}
        </code>
      </div>

      <p className="mt-4 text-xs font-medium uppercase tracking-wide text-ink-soft">Then do this</p>
      <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {ACTION_TOGGLES.map(({ key, label }) => (
          <label
            key={key}
            className={cx(
              'flex items-center gap-2 text-[13px]',
              suppressed.has(key) ? 'cursor-not-allowed text-ink-soft' : 'cursor-pointer text-ink'
            )}
            title={suppressed.has(key) ? 'Another action already decides where this mail goes' : undefined}
          >
            <input
              type="checkbox"
              checked={!!draft.actions[key]}
              onChange={setAction(key)}
              disabled={suppressed.has(key)}
              className="ws-focus h-4 w-4 accent-[var(--ws-accent)]"
            />
            {label}
          </label>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Apply labels" hint="Comma separated — labels must already exist">
          <Input
            value={draft.labelsText}
            onChange={(event) => onChange({ ...draft, labelsText: event.target.value })}
            placeholder="Newsletters, Receipts"
          />
        </Field>
        <Field label="Set category">
          <Select value={draft.actions.setCategory} onChange={setAction('setCategory')} className="w-full">
            {FILTER_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Forward to" hint="Leave blank to not forward" className="sm:col-span-2">
          <Input
            value={draft.actions.forwardTo}
            onChange={setAction('forwardTo')}
            placeholder="assistant@example.com"
          />
        </Field>
      </div>

      {errors.length > 0 && (
        <ul className="mt-4 space-y-1 rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      {previewError && (
        <p className="mt-3 rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          {previewError}
        </p>
      )}

      {preview && (
        <div className="mt-3 rounded-lg border border-hairline p-3">
          <p className="text-xs text-ink-soft">
            Matches <strong className="text-ink">{preview.total}</strong> stored message
            {preview.total === 1 ? '' : 's'}
            {preview.total > preview.matches.length ? ` — showing ${preview.matches.length}` : ''}
          </p>
          <ul className="mt-2 space-y-1">
            {preview.matches.map((mail) => (
              <li key={mail._id} className="flex min-w-0 items-baseline gap-2 text-xs">
                <span className="w-40 shrink-0 truncate text-ink-soft">{mail.from}</span>
                <span className="min-w-0 flex-1 truncate text-ink">{mail.subject || '(no subject)'}</span>
                <span className="shrink-0 text-ink-soft">{mail.folder}</span>
              </li>
            ))}
            {preview.matches.length === 0 && (
              <li className="text-xs text-ink-soft">
                Nothing stored matches yet — the filter will still run on new mail.
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => onChange({ ...draft, fields: { ...EMPTY_FIELDS } })}>
          Clear conditions
        </Button>
        <Button variant="ghost" onClick={runTest} disabled={!query || testing}>
          {testing ? 'Testing…' : 'Test conditions'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={errors.length > 0 || saving}>
          {saving ? 'Saving…' : draft._id ? 'Save changes' : 'Create filter'}
        </Button>
      </div>
    </form>
  );
};

export default FilterEditor;
